import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Building2, MapPin, Phone, Anchor, User } from "lucide-react";
import { FadeIn, SectionHeading } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FALLBACK = [
  {
    id: "alappuzha",
    name: "Alappuzha",
    tag: "Head Office",
    focus: "Corporate HQ — Manufacturing, Import, Export & Distribution",
    address: "Kainakari Building, 2nd Floor (above ESAF Bank), Near Powerhouse, Alappuzha, Kerala 688007, India",
    phone: "+91 79074 40408 • +91 96050 96999",
  },
  {
    id: "cherthala",
    name: "Cherthala",
    tag: "Branch Office",
    focus: "Distribution & regional operations",
    address: "Cherthala, Kerala, India — contact the head office for directions",
    phone: "+91 79074 40408",
  },
  {
    id: "aroor",
    name: "Aroor",
    tag: "Branch Office",
    focus: "Distribution & logistics",
    address: "Aroor, Kerala, India — contact the head office for directions",
    phone: "+91 79074 40408",
  },
  {
    id: "ambalappuzha",
    name: "Ambalappuzha",
    tag: "Branch Office",
    focus: "Operations & support",
    address: "Ambalappuzha, Kerala, India — contact the head office for directions",
    phone: "+91 96050 96999",
  },
  {
    id: "kottayam",
    name: "Kottayam",
    tag: "Branch Office",
    focus: "Operations & support",
    address: "Kottayam, Kerala, India — contact the head office for directions",
    phone: "+91 96050 96999",
  },
];

const normalize = (d) => ({
  id: d._id || d.id,
  name: d.name,
  tag: d.is_hq ? "Head Office" : "Branch Office",
  focus: d.focus || "",
  address: d.address || "",
  phone: d.phone || "",
  manager: d.manager || "",
});

export const Branches = () => {
  const [branches, setBranches] = useState(FALLBACK);
  const [active, setActive] = useState("alappuzha");

  useEffect(() => {
    const loadBranches = () => {
      fetch(`${API}/branches`, { cache: "no-store" })
        .then((r) => (r.ok ? r.json() : Promise.reject()))
        .then((docs) => {
          if (Array.isArray(docs) && docs.length) {
            const list = docs.map(normalize);
            setBranches(list);
            setActive((cur) => (list.some((b) => b.id === cur) ? cur : list[0]?.id));
          }
        })
        .catch(() => {});
    };
    loadBranches();
    window.addEventListener("focus", loadBranches);
    return () => window.removeEventListener("focus", loadBranches);
  }, []);

  const branch = branches.find((b) => b.id === active) || branches[0];

  return (
    <section id="branches" className="relative bg-surface py-20 sm:py-28 lg:py-36">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <SectionHeading
          kicker="04 — Our Branches"
          title="Five major hubs. 67+ branches strong."
          description="Our five major branches below anchor a network of 67+ branches across Kerala — each one a physical office you can walk into."
        />

        <div className="grid grid-cols-1 gap-8 lg:grid-cols-12">
          <FadeIn className="lg:col-span-5">
            <div className="flex flex-col gap-3" data-testid="branch-selector">
              {branches.map((b) => (
                <button
                  key={b.id}
                  data-testid={`branch-tab-${b.name.toLowerCase().replace(/\s+/g, "-")}`}
                  onClick={() => setActive(b.id)}
                  className={`flex items-center justify-between rounded-2xl border p-5 text-left transition-all duration-300 sm:p-6 ${
                    active === b.id
                      ? "border-brand-red/60 bg-brand-red/[0.08]"
                      : "border-white/10 bg-white/[0.03] hover:border-white/25"
                  }`}
                >
                  <div>
                    <p className="font-display text-xl font-semibold text-white sm:text-2xl">{b.name}</p>
                    <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-brand-bright">{b.tag}</p>
                  </div>
                  <MapPin size={20} className={active === b.id ? "text-brand-red" : "text-slate-600"} />
                </button>
              ))}
              <div className="mt-2 rounded-2xl border border-dashed border-white/15 p-5 sm:p-6">
                <p className="flex items-center gap-3 text-sm text-slate-400">
                  <Anchor size={18} className="shrink-0 text-brand-red" />
                  …and a network of 67+ branches across Kerala. Ask at the head
                  office for the branch nearest you.
                </p>
              </div>
            </div>
          </FadeIn>

          <FadeIn delay={0.12} className="lg:col-span-7">
            <div className="relative h-full overflow-hidden rounded-2xl border border-white/10 bg-white/[0.04] p-6 sm:p-10">
              <div className="red-glow absolute -right-24 -top-24 h-72 w-72" />
              <AnimatePresence mode="wait">
                <motion.div
                  key={branch.id}
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -16 }}
                  transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
                  data-testid={`branch-detail-${branch.id}`}
                  className="relative flex h-full flex-col justify-between gap-10"
                >
                  <div>
                    <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-brand-red">
                      {branch.tag}
                    </p>
                    <h3 className="mt-3 font-display text-3xl font-semibold text-white sm:text-4xl">
                      {branch.name}
                    </h3>
                    <p className="mt-4 text-sm leading-relaxed text-slate-300 sm:text-base">
                      <Building2 size={15} className="mr-2 inline text-brand-red" />
                      {branch.focus}
                    </p>
                    {branch.manager && (
                      <p className="mt-2 text-sm text-slate-300 sm:text-base">
                        <User size={15} className="mr-2 inline text-brand-red" />
                        Branch Manager — {branch.manager}
                      </p>
                    )}
                  </div>
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div className="rounded-xl border border-white/10 bg-ink/50 p-5">
                      <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">Address</p>
                      <p className="mt-2 text-sm leading-relaxed text-slate-200">{branch.address}</p>
                    </div>
                    <div className="rounded-xl border border-white/10 bg-ink/50 p-5">
                      <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">Phone</p>
                      <p className="mt-2 text-sm text-slate-200">{branch.phone}</p>
                      <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">Hours</p>
                      <p className="mt-2 text-sm text-slate-200">Mon – Sat, 9:00 AM – 6:00 PM</p>
                    </div>
                  </div>
                </motion.div>
              </AnimatePresence>
            </div>
          </FadeIn>
        </div>
      </div>
    </section>
  );
};

export default Branches;
